/**
 * 환경변수 가져오기
 */
function getEnv(key: string): string | undefined {
  const value = (import.meta.env as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : undefined;
}

/**
 * 환경 설정
 */
export const config = {
  /** API 서버 URL. 미설정 시 ''(같은 오리진) → 개발 시 Vite 프록시(/api/portal→3100) 사용으로 CORS/대기 방지 */
  get apiUrl(): string {
    const v = getEnv('VITE_API_URL');
    if (v !== undefined && v !== '') return v;
    return '';
  },

  /** 랜딩 페이지 URL */
  get landingUrl(): string {
    return getEnv('VITE_LANDING_URL') || 'http://localhost:3002';
  },

  /** 포탈 URL. 프로덕션 미설정 시 ''(같은 오리진) → Entry/서비스 카드 링크가 상대 경로로 동작 */
  get portalUrl(): string {
    const v = getEnv('VITE_PORTAL_URL');
    if (v !== undefined && v !== '') return v;
    return import.meta.env.PROD ? '' : 'http://localhost:3000';
  },

  get portalApiUrl(): string {
    const v = getEnv('VITE_PORTAL_API_URL');
    if (v !== undefined && v !== '') return v;
    return import.meta.env.PROD ? '' : 'http://localhost:3100';
  },

  /** ITops Agent Controller URL. 미설정 시 같은 오리진(/admin/* reverse proxy) */
  get itopsAgentControllerUrl(): string {
    const v = getEnv('VITE_ITOPS_AGENT_CONTROLLER_URL');
    if (v !== undefined && v !== '') return v.replace(/\/+$/, '');
    return '';
  },

  /** Cognito Hosted UI domain for ITops Agent admin auth */
  get itopsAgentCognitoDomain(): string {
    const v = getEnv('VITE_ITOPS_AGENT_COGNITO_DOMAIN');
    if (v !== undefined && v !== '') return v.replace(/\/+$/, '');
    return '';
  },

  get itopsAgentCognitoClientId(): string {
    return getEnv('VITE_ITOPS_AGENT_COGNITO_CLIENT_ID') || '';
  },

  get itopsAgentCognitoScopes(): string {
    return getEnv('VITE_ITOPS_AGENT_COGNITO_SCOPES') || 'openid profile email';
  },

  get itopsAgentCognitoRedirectUri(): string {
    const v = getEnv('VITE_ITOPS_AGENT_COGNITO_REDIRECT_URI');
    if (v !== undefined && v !== '') return v;
    if (typeof window !== 'undefined') {
      return `${window.location.origin}/admin/itops-agent/callback`;
    }
    return '/admin/itops-agent/callback';
  },

  get itopsAgentCognitoLogoutUri(): string {
    const v = getEnv('VITE_ITOPS_AGENT_COGNITO_LOGOUT_URI');
    if (v !== undefined && v !== '') return v;
    if (typeof window !== 'undefined') {
      return `${window.location.origin}/admin/itops-agent`;
    }
    return '/admin/itops-agent';
  },

  /** Entry 페이지 URL */
  get entryUrl(): string {
    const v = getEnv('VITE_ENTRY_URL');
    if (v !== undefined && v !== '') return v;
    return import.meta.env.PROD ? '/entry' : 'http://localhost:3000/entry';
  },

  /** CMDB 서비스 URL */
  get cmdbUrl(): string {
    return getEnv('VITE_CMDB_URL') || 'http://localhost:5105/cmdb/';
  },

  /** 개발 모드 여부 */
  get isDev(): boolean {
    return import.meta.env.DEV === true;
  },

  /** 프로덕션 모드 여부 */
  get isProd(): boolean {
    return import.meta.env.PROD === true;
  },
} as const;

/**
 * 서비스별 URL 생성
 * - 로컬: `http://localhost:{port}{path}` (path 예: `/iam-users/`, `/iam-users-admin/`)
 * - 운영: `{portalUrl}{path}` (동일 path — Nginx/CloudFront와 일치)
 */
export function getServiceUrl(port: number, path = ''): string {
  const baseUrl = config.isDev
    ? `http://localhost:${port}`
    : config.portalUrl; // 프로덕션에서는 같은 도메인 사용
  return `${baseUrl}${path}`;
}

/** Portal API prefix (Backend-API-이름정책) */
const PORTAL_API_PREFIX = '/api/portal';

/** Portal 오리진만 있고 `/api` 접미사가 없는 legacy 빌드 보정 */
function normalizeServiceApiBase(v: string): string {
  const trimmed = v.replace(/\/$/, '');
  if (!trimmed) return import.meta.env.PROD ? '/api' : '';
  if (trimmed === '/api' || trimmed.endsWith('/api')) {
    return trimmed.startsWith('/') || /^https?:\/\//i.test(trimmed) ? trimmed : `/${trimmed}`;
  }
  if (/^https?:\/\//i.test(trimmed)) return `${trimmed}/api`;
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

/**
 * 서비스 SPA API base (CloudFront same-origin: `/api`)
 * @param devFallback 로컬 dev — 예: `http://localhost:5001/api`
 */
export function getServiceApiBaseUrl(devFallback: string): string {
  const v = getEnv('VITE_API_URL');
  if (import.meta.env.DEV && v && /itops-portal\.iampam\.io|itops-demo\.iampam\.io/i.test(v)) {
    return normalizeServiceApiBase(devFallback);
  }
  if (v !== undefined && v !== '') return normalizeServiceApiBase(v);
  if (import.meta.env.PROD) return '/api';
  return normalizeServiceApiBase(devFallback);
}

/**
 * Excel-backed service API paths (`/api/{segment}/...`).
 * Legacy prod builds with VITE_API_URL=portal origin (no /api) must not use `/{segment}` alone.
 */
export function getServiceExcelApiPaths(
  serviceSegment: string,
  devFallback: string,
): { apiBase: string; prefix: string } {
  const apiBase = getServiceApiBaseUrl(devFallback).replace(/\/$/, '');
  if (apiBase.endsWith('/api')) {
    return { apiBase, prefix: `/${serviceSegment}` };
  }
  if (!apiBase || apiBase === '/api') {
    return { apiBase: apiBase || '', prefix: `/api/${serviceSegment}` };
  }
  return { apiBase, prefix: `/api/${serviceSegment}` };
}

/**
 * API 엔드포인트 URL 생성
 * - apiUrl 미설정: `/api/portal/...` (CloudFront same-origin)
 * - apiUrl이 `/api`로 끝남: `{base}/portal/...` (이중 `/api` 방지)
 */
export function getApiEndpoint(path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  const portalPath = `/portal${p}`;
  const base = (config.apiUrl || '').replace(/\/$/, '');
  if (!base) return `/api${portalPath}`;
  if (base.endsWith('/api')) return `${base}${portalPath}`;
  return `${base}${PORTAL_API_PREFIX}${p}`;
}
